/**
 * Visualizations Tab - CRUD interface for Plotly.js visualizations.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import * as api from '../../api/analytics';
import { renderVisualization, showLoading, renderFigure } from '../../lib/vizRunner';
import { executeVizCode } from '../../lib/vizExecutor';
import VizCodeEditor from '../viz/VizCodeEditor';
import ErrorPanel from '../viz/ErrorPanel';
import ConsolePanel from '../viz/ConsolePanel';
import DataSchemaPanel from '../viz/DataSchemaPanel';
import VizPivotBuilder from '../viz/VizPivotBuilder';
import { parsePivotConfig } from '../../lib/vizPivotGenerator';

// Default visualization template
const DEFAULT_VIZ_CODE = `// Visualization Code
// This function receives data from your views and returns a Plotly spec

function render(data, params) {
  // data is an object with view slugs as keys
  // e.g., data.my_view = [{ Id: '...', Name: '...', ... }, ...]

  // Extract the first view's data
  const viewData = Object.values(data)[0] || [];

  // Build a simple bar chart
  return {
    data: [{
      type: 'bar',
      x: viewData.map(row => row.Name || row.Id || 'Unknown'),
      y: viewData.map((_, i) => i + 1),
    }],
    layout: {
      title: 'Sample Visualization',
      xaxis: { title: 'Name' },
      yaxis: { title: 'Count' },
    }
  };
}`;

// Extract "table Foo not synced" from an API error message.
function extractMissingTable(err) {
  const msg = err?.data?.message || err?.message || '';
  const m = msg.match(/table (\S+) not synced/i);
  return m ? m[1] : null;
}

export default function VisualizationsTab() {
  const [visualizations, setVisualizations] = useState([]);
  const [views, setViews] = useState([]);
  const [selectedViz, setSelectedViz] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditor, setShowEditor] = useState(false);

  // Editor state
  const [editorName, setEditorName] = useState('');
  const [editorDescription, setEditorDescription] = useState('');
  const [editorCategory, setEditorCategory] = useState('');
  const [editorRequiredViews, setEditorRequiredViews] = useState([]);
  const [editorCode, setEditorCode] = useState(DEFAULT_VIZ_CODE);
  const [editorError, setEditorError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isRendering, setIsRendering] = useState(false);

  // Execution results
  const [executionError, setExecutionError] = useState(null);
  const [consoleLogs, setConsoleLogs] = useState([]);

  // Schema cache: { [viewId]: viewObject }
  const [viewSchemas, setViewSchemas] = useState({});
  // Track in-flight schema fetches to avoid duplicate requests
  const fetchingViewsRef = useRef(new Set());

  // Editor mode: 'pivot' | 'code'
  const [editorMode, setEditorMode] = useState('code');
  const [showModeConfirm, setShowModeConfirm] = useState(false);

  // Monaco editor ref (set via onEditorMount)
  const monacoEditorRef = useRef(null);
  const monacoRef = useRef(null);

  // Preview
  const previewRef = useRef(null);
  const viewerRef = useRef(null);
  const [showViewer, setShowViewer] = useState(false);
  const [viewerViz, setViewerViz] = useState(null);
  // Rich error state for the viewer: null = no error, string = missing table name
  const [viewerMissingTable, setViewerMissingTable] = useState(null);
  const [viewerError, setViewerError] = useState(null);

  // Background health-check state: { [vizId]: { ok: bool, missingTable: string|null } }
  const [vizStatuses, setVizStatuses] = useState({});

  // Folder state: set of folder names that are collapsed
  const [collapsedFolders, setCollapsedFolders] = useState(new Set());

  // Repair missing tables
  const [isRepairingTables, setIsRepairingTables] = useState(false);
  const [repairToast, setRepairToast] = useState(null); // { message, type: 'success'|'info'|'error' }

  // Gallery panel
  const [showGallery, setShowGallery] = useState(false);
  const [galleryItems, setGalleryItems] = useState([]);
  const [isLoadingGallery, setIsLoadingGallery] = useState(false);
  const [galleryError, setGalleryError] = useState(null);
  const [installingRefId, setInstallingRefId] = useState(null); // ref_id currently being installed

  // Load visualizations and views
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [vizResponse, viewsResponse] = await Promise.all([
        api.fetchVisualizations(),
        api.fetchViews(),
      ]);
      const vizList = vizResponse?.visualizations || [];
      setVisualizations(vizList);
      setViews(viewsResponse?.views || []);
      // Kick off background status checks after state settles
      return vizList;
    } catch (err) {
      setError(err.message || 'Failed to load data');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Background health-check: render each viz quietly and record pass/fail.
  // Runs in batches of 5 to avoid hammering the API.
  // NOTE: the backend now LEFT JOINs missing tables (no error thrown) — check
  // response.missing_tables for the indicator, not just catch blocks.
  const runStatusChecks = useCallback(async (vizList) => {
    if (!vizList.length) return;
    setVizStatuses({});
    const BATCH = 5;
    for (let i = 0; i < vizList.length; i += BATCH) {
      const batch = vizList.slice(i, i + BATCH);
      await Promise.all(
        batch.map(async (viz) => {
          try {
            const response = await api.renderVisualization(viz.id, {});
            const missing = response?.missing_tables?.[0] || null;
            setVizStatuses((prev) => ({
              ...prev,
              [viz.id]: { ok: !missing, missingTable: missing },
            }));
          } catch (err) {
            const missingTable = extractMissingTable(err);
            setVizStatuses((prev) => ({
              ...prev,
              [viz.id]: { ok: false, missingTable },
            }));
          }
        })
      );
    }
  }, []);

  useEffect(() => {
    loadData().then((vizList) => {
      // Don't await — let it run in the background
      runStatusChecks(vizList);
    });
  }, [loadData, runStatusChecks]);

  // Group visualizations into folders. Null/empty category → "Main" (always first).
  const folders = useMemo(() => {
    const map = new Map();
    map.set('Main', []);
    for (const viz of visualizations) {
      const folder = viz.category?.trim() || 'Main';
      if (!map.has(folder)) map.set(folder, []);
      map.get(folder).push(viz);
    }
    // Remove empty Main if there are no uncategorized vizzes
    if (map.get('Main').length === 0 && map.size > 1) map.delete('Main');
    return map;
  }, [visualizations]);

  const toggleFolder = (name) => {
    setCollapsedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  // Collect all unique missing table names across all vizzes
  const missingTables = useMemo(() => {
    const tables = new Set();
    for (const status of Object.values(vizStatuses)) {
      if (!status.ok && status.missingTable) tables.add(status.missingTable);
    }
    return [...tables];
  }, [vizStatuses]);

  // Count of vizzes that are failing due to missing tables
  const missingCount = useMemo(
    () => Object.values(vizStatuses).filter((s) => !s.ok && s.missingTable).length,
    [vizStatuses]
  );

  // Repair: enable sync configs + trigger workflows for all missing tables
  const handleRepairMissingTables = async () => {
    if (missingTables.length === 0) return;
    setIsRepairingTables(true);
    setRepairToast(null);
    try {
      const result = await api.repairMissingTables(missingTables);
      const triggered = result.results?.filter((r) => ['sync_triggered', 'auto_configured'].includes(r.action)).length || 0;
      const needsSetup = result.results?.filter((r) => r.action === 'needs_setup').length || 0;
      let msg = `Sync started for ${triggered} table(s).`;
      if (needsSetup > 0) msg += ` ${needsSetup} table(s) need manual setup in Sync Config.`;
      setRepairToast({ message: msg, type: needsSetup > 0 ? 'info' : 'success' });
      setTimeout(() => setRepairToast(null), 8000);
    } catch (err) {
      setRepairToast({ message: err.message || 'Failed to start sync', type: 'error' });
      setTimeout(() => setRepairToast(null), 6000);
    } finally {
      setIsRepairingTables(false);
    }
  };

  // Gallery: load reference visualizations
  const handleOpenGallery = async () => {
    setShowGallery(true);
    if (galleryItems.length > 0) return; // already loaded
    setIsLoadingGallery(true);
    setGalleryError(null);
    try {
      const res = await api.fetchReferenceVisualizations();
      setGalleryItems(res?.visualizations || res?.items || []);
    } catch (err) {
      setGalleryError(err.message || 'Failed to load gallery');
    } finally {
      setIsLoadingGallery(false);
    }
  };

  // Check if a gallery item is already installed (source='reference' with matching source_ref)
  const installedRefIds = useMemo(() => {
    const ids = new Set();
    for (const viz of visualizations) {
      if (viz.source === 'reference' && viz.source_ref) ids.add(viz.source_ref);
    }
    return ids;
  }, [visualizations]);

  // Install a gallery bundle into the tenant
  const handleInstallFromGallery = async (refId) => {
    setInstallingRefId(refId);
    try {
      await api.installReferenceBundle(refId, { skipExisting: false });
      const vizList = await loadData();
      runStatusChecks(vizList);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to install visualization');
    } finally {
      setInstallingRefId(null);
    }
  };

  // Open editor for new visualization — default to code mode
  const handleNewViz = () => {
    setSelectedViz(null);
    setEditorName('');
    setEditorDescription('');
    setEditorCategory('');
    setEditorRequiredViews([]);
    setEditorCode(DEFAULT_VIZ_CODE);
    setEditorError(null);
    setExecutionError(null);
    setConsoleLogs([]);
    setEditorMode('code');
    setShowEditor(true);
  };

  // Open editor for existing visualization
  const handleEditViz = async (vizId) => {
    try {
      const response = await api.fetchVisualization(vizId);
      const viz = response;
      setSelectedViz(viz);
      setEditorName(viz.name);
      setEditorDescription(viz.description || '');
      setEditorCategory(viz.category || '');
      setEditorRequiredViews(viz.required_views || []);
      setEditorCode(viz.js_content);
      setEditorError(null);
      setExecutionError(null);
      setConsoleLogs([]);
      // Auto-detect mode: pivot if code has /*@pivot…*/ prefix, otherwise code
      setEditorMode(parsePivotConfig(viz.js_content) ? 'pivot' : 'code');
      setShowEditor(true);

      // Pre-fetch schemas for views already on this viz
      if (viz.required_views?.length) {
        viz.required_views.forEach((viewId) => {
          fetchViewSchema(viewId);
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to load visualization');
    }
  };

  // Fetch and cache schema for a single view (best-effort, deduplicated)
  const fetchViewSchema = useCallback(async (viewId) => {
    if (fetchingViewsRef.current.has(viewId)) return;
    fetchingViewsRef.current.add(viewId);
    try {
      const viewData = await api.fetchView(viewId);
      setViewSchemas((prev) => ({ ...prev, [viewId]: viewData }));
    } catch {
      // best-effort — schema panel just won't show for this view
      fetchingViewsRef.current.delete(viewId);
    }
  }, []);

  // Save visualization
  const handleSave = async () => {
    if (!editorName.trim()) {
      setEditorError('Name is required');
      return;
    }
    if (editorRequiredViews.length === 0) {
      setEditorError('At least one view is required');
      return;
    }
    if (!editorCode.trim()) {
      setEditorError('Visualization code is required');
      return;
    }

    setIsSaving(true);
    setEditorError(null);
    try {
      if (selectedViz) {
        await api.updateVisualization(selectedViz.id, {
          name: editorName,
          description: editorDescription,
          category: editorCategory || null,
          required_views: editorRequiredViews,
          js_content: editorCode,
        });
      } else {
        await api.createVisualization({
          name: editorName,
          description: editorDescription,
          category: editorCategory || null,
          required_views: editorRequiredViews,
          js_content: editorCode,
        });
      }

      const vizList = await loadData();
      runStatusChecks(vizList);
      setShowEditor(false);
    } catch (err) {
      setEditorError(err.response?.data?.message || err.message || 'Failed to save visualization');
    } finally {
      setIsSaving(false);
    }
  };

  // Preview visualization using Worker-based execution
  const handlePreview = async () => {
    if (!selectedViz || editorRequiredViews.length === 0) {
      setEditorError('Save the visualization first to preview');
      return;
    }

    setIsRendering(true);
    setEditorError(null);
    setExecutionError(null);
    setConsoleLogs([]);

    if (previewRef.current) {
      showLoading(previewRef.current);
    }

    try {
      const response = await api.renderVisualization(selectedViz.id, {});
      const { figure, error: execError, logs } = await executeVizCode(
        response.code,
        response.data,
        response.params
      );

      setConsoleLogs(logs || []);

      if (execError) {
        setExecutionError(execError);
        if (previewRef.current) {
          previewRef.current.innerHTML = '';
        }
        return;
      }

      if (previewRef.current) {
        const { error: plotlyError } = await renderFigure(previewRef.current, figure);
        if (plotlyError) {
          setExecutionError(plotlyError);
        }
      }
    } catch (err) {
      setEditorError(err.message || 'Failed to render visualization');
    } finally {
      setIsRendering(false);
    }
  };

  // View visualization (quick preview without editor)
  const handleViewViz = async (vizId) => {
    setIsRendering(true);
    setShowViewer(true);
    setShowEditor(false);
    setViewerMissingTable(null);
    setViewerError(null);

    try {
      const viz = await api.fetchVisualization(vizId);
      setViewerViz(viz);
      const response = await api.renderVisualization(vizId, {});

      // Backend uses LEFT JOINs for missing tables — check missing_tables field.
      const missingTable = response?.missing_tables?.[0] || null;
      if (missingTable) {
        setViewerMissingTable(missingTable);
        setVizStatuses((prev) => ({ ...prev, [vizId]: { ok: false, missingTable } }));
      } else {
        // Wait for ref to be ready then render
        setTimeout(() => {
          if (viewerRef.current) {
            renderVisualization(viewerRef.current, response);
          }
        }, 100);
      }
    } catch (err) {
      const missingTable = extractMissingTable(err);
      if (missingTable) {
        setViewerMissingTable(missingTable);
        setVizStatuses((prev) => ({ ...prev, [vizId]: { ok: false, missingTable } }));
      } else {
        setViewerError(err.message || 'Failed to render visualization');
      }
    } finally {
      setIsRendering(false);
    }
  };

  // Delete visualization
  const handleDelete = async (vizId) => {
    if (!window.confirm('Are you sure you want to delete this visualization?')) return;

    try {
      await api.deleteVisualization(vizId);
      const vizList = await loadData();
      runStatusChecks(vizList);
      if (selectedViz?.id === vizId) {
        setShowEditor(false);
        setSelectedViz(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete visualization');
    }
  };

  // Clone visualization
  const handleClone = async (vizId) => {
    try {
      const result = await api.cloneVisualization(vizId);
      const vizList = await loadData();
      runStatusChecks(vizList);
      if (result.id) {
        handleEditViz(result.id);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to clone visualization');
    }
  };

  // Export visualization
  const handleExport = async (vizId) => {
    try {
      const exportData = await api.exportVisualizations([vizId], true);
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const viz = visualizations.find(v => v.id === vizId);
      a.download = `${viz?.slug || 'visualization'}_export.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to export visualization');
    }
  };

  // Import visualization from file
  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const exportData = JSON.parse(text);
      const result = await api.importVisualizations(exportData);

      if (result.errors?.length > 0) {
        setError(`Import completed with warnings: ${result.errors.join(', ')}`);
      }

      const vizList = await loadData();
      runStatusChecks(vizList);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to import visualization');
    }

    event.target.value = '';
  };

  // Toggle view selection + fetch schema
  const handleToggleView = (viewId) => {
    setEditorRequiredViews((prev) =>
      prev.includes(viewId)
        ? prev.filter((id) => id !== viewId)
        : [...prev, viewId]
    );
    // Fetch schema for this view (fetchViewSchema deduplicates)
    fetchViewSchema(viewId);
  };

  // Get view name by ID
  const getViewName = (viewId) => {
    const view = views.find((v) => v.id === viewId);
    return view?.name || viewId;
  };

  // Rich "needs sync" error panel shown in the viewer when a table isn't synced
  const renderMissingSyncPanel = (tableName, onClose) => (
    <div className="flex flex-col items-center justify-center h-full p-8">
      <div className="max-w-lg w-full bg-white border border-amber-200 rounded-xl shadow-sm p-8 flex flex-col items-center gap-5">
        <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
          <span className="text-amber-600 text-2xl">⚠</span>
        </div>
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {viewerViz?.name || 'This visualization'} needs data to be synced
          </h3>
          <p className="text-sm text-gray-500">
            The underlying view queries a table that hasn't been synced yet.
          </p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg">
          <span className="text-amber-700 font-mono font-medium text-sm">{tableName}</span>
        </div>
        <p className="text-sm text-gray-600 text-center">
          Go to the <strong>Sync Config</strong> tab, enable the <strong>{tableName}</strong> object,
          and run a sync. Then come back to view this visualization.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition"
          >
            ← Back to list
          </button>
        </div>
      </div>
    </div>
  );

  // Monaco editor mount callback
  const handleEditorMount = (editor, monaco) => {
    monacoEditorRef.current = editor;
    monacoRef.current = monaco;
  };

  // Go to a line in the Monaco editor
  const handleGoToLine = (line) => {
    const editor = monacoEditorRef.current;
    if (!editor || !line) return;
    editor.revealLineInCenter(line);
    editor.setPosition({ lineNumber: line, column: 1 });
    editor.focus();
  };

  // Insert JSDoc schema comment at top of editor (replacing existing block if present)
  const handleInsertSchema = (comment) => {
    const editor = monacoEditorRef.current;
    const monaco = monacoRef.current;
    if (!editor || !monaco) return;

    const model = editor.getModel();
    if (!model) return;

    const text = model.getValue();
    const jsdocPattern = /^\/\*\*[\s\S]*?\*\//;
    const existingMatch = text.match(jsdocPattern);

    if (existingMatch) {
      // Replace existing JSDoc block
      const startPos = model.getPositionAt(0);
      const endPos = model.getPositionAt(existingMatch[0].length);
      editor.executeEdits('insert-schema', [{
        range: new monaco.Range(
          startPos.lineNumber, startPos.column,
          endPos.lineNumber, endPos.column
        ),
        text: comment,
      }]);
    } else {
      // Prepend at line 1
      editor.executeEdits('insert-schema', [{
        range: new monaco.Range(1, 1, 1, 1),
        text: comment + '\n',
      }]);
    }
    editor.focus();
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">Visualizations</h2>
        <div className="flex gap-2">
          <button
            onClick={handleOpenGallery}
            className="px-4 py-2 border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-50 transition"
          >
            ✦ Gallery
          </button>
          <label className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition">
            Import
            <input
              type="file"
              accept=".json"
              onChange={handleImportFile}
              className="hidden"
            />
          </label>
          <button
            onClick={handleNewViz}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
          >
            + New Visualization
          </button>
        </div>
      </div>

      {/* Repair toast */}
      {repairToast && (
        <div className={`p-3 rounded-lg text-sm flex items-center justify-between ${
          repairToast.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' :
          repairToast.type === 'error' ? 'bg-red-50 border border-red-200 text-red-700' :
          'bg-blue-50 border border-blue-200 text-blue-800'
        }`}>
          <span>{repairToast.message}</span>
          <button onClick={() => setRepairToast(null)} className="ml-4 text-current opacity-60 hover:opacity-100">✕</button>
        </div>
      )}

      {/* Missing tables banner — shown after status checks complete */}
      {missingCount > 0 && !isRepairingTables && (
        <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-2 text-sm text-amber-800">
            <span>⚠</span>
            <span>
              <strong>{missingCount}</strong> visualization{missingCount !== 1 ? 's' : ''} need{missingCount === 1 ? 's' : ''} data synced
              {' '}({missingTables.length} table{missingTables.length !== 1 ? 's' : ''}: {missingTables.map(t => {
                const obj = t.includes('__') ? t.split('__').slice(1).join('__') : t;
                return obj;
              }).join(', ')})
            </span>
          </div>
          <button
            onClick={handleRepairMissingTables}
            disabled={isRepairingTables}
            className="px-3 py-1.5 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700 disabled:opacity-50 transition shrink-0 ml-4"
          >
            Sync All Missing Tables
          </button>
        </div>
      )}
      {isRepairingTables && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          Starting sync workflows...
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Quick Viewer Modal */}
      {showViewer && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div className="flex justify-between items-center border-b pb-4">
            <h3 className="text-lg font-medium">
              {viewerViz?.name || 'Visualization'}
            </h3>
            <button
              onClick={() => { setShowViewer(false); setViewerMissingTable(null); setViewerError(null); }}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              &times;
            </button>
          </div>

          {viewerViz?.description && (
            <p className="text-sm text-gray-500">{viewerViz.description}</p>
          )}

          {isRendering ? (
            <div className="h-96 flex items-center justify-center text-gray-500">
              Rendering visualization...
            </div>
          ) : viewerMissingTable ? (
            <div className="h-96">
              {renderMissingSyncPanel(viewerMissingTable, () => {
                setShowViewer(false);
                setViewerMissingTable(null);
              })}
            </div>
          ) : viewerError ? (
            <div className="h-96 flex items-center justify-center">
              <div className="text-red-500 text-sm">{viewerError}</div>
            </div>
          ) : (
            <div
              ref={viewerRef}
              className="h-96 border rounded-lg bg-gray-50"
            />
          )}

          <div className="flex gap-3">
            <button
              onClick={() => {
                setShowViewer(false);
                setViewerMissingTable(null);
                setViewerError(null);
                handleEditViz(viewerViz?.id);
              }}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Edit
            </button>
            <button
              onClick={() => { setShowViewer(false); setViewerMissingTable(null); setViewerError(null); }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {!showViewer && showEditor ? (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div className="flex justify-between items-center border-b pb-4">
            <h3 className="text-lg font-medium">
              {selectedViz ? 'Edit Visualization' : 'New Visualization'}
            </h3>
            <button
              onClick={() => setShowEditor(false)}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              &times;
            </button>
          </div>

          {/* Name & Category */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                value={editorName}
                onChange={(e) => setEditorName(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="My Visualization"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <input
                type="text"
                value={editorCategory}
                onChange={(e) => setEditorCategory(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Sales, Pipeline, etc."
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <input
              type="text"
              value={editorDescription}
              onChange={(e) => setEditorDescription(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="What does this visualization show?"
            />
          </div>

          {/* Required Views */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data Sources (Views) *
            </label>
            {views.length === 0 ? (
              <div className="text-sm text-gray-500 p-3 bg-gray-50 rounded-lg">
                No views available. Create a view first to use as a data source.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {views.map((view) => (
                  <button
                    key={view.id}
                    onClick={() => handleToggleView(view.id)}
                    className={`px-3 py-1 rounded-full text-sm transition ${
                      editorRequiredViews.includes(view.id)
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {view.name}
                  </button>
                ))}
              </div>
            )}
            {editorRequiredViews.length > 0 && (
              <div className="mt-2 text-sm text-gray-500">
                Selected: {editorRequiredViews.map(getViewName).join(', ')}
              </div>
            )}
          </div>

          {/* Mode Toggle */}
          <div className="flex items-center gap-3">
            <div className="flex border border-gray-300 rounded-lg overflow-hidden w-fit">
              <button
                onClick={() => setEditorMode('pivot')}
                className={`px-3 py-1.5 text-sm font-medium transition ${
                  editorMode === 'pivot'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Pivot
              </button>
              <button
                onClick={() => {
                  if (editorMode === 'code') return;
                  setShowModeConfirm(true);
                }}
                className={`px-3 py-1.5 text-sm font-medium transition ${
                  editorMode === 'code'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Code
              </button>
            </div>
            {editorMode === 'code' && (
              <span className="text-xs text-gray-400">Ctrl+Enter to preview</span>
            )}
          </div>

          {/* Mode-switch confirmation (pivot → code only, since it overwrites generated code) */}
          {showModeConfirm && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-center gap-3">
              <span>Switch to code editor? The pivot builder will no longer track your changes.</span>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => { setEditorMode('code'); setShowModeConfirm(false); }}
                  className="px-3 py-1 bg-amber-600 text-white rounded hover:bg-amber-700 text-xs"
                >
                  Switch
                </button>
                <button
                  onClick={() => setShowModeConfirm(false)}
                  className="px-3 py-1 border border-amber-400 rounded hover:bg-amber-100 text-xs"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Pivot Builder or Code Editor */}
          {editorMode === 'pivot' ? (
            <VizPivotBuilder
              viewSchemas={viewSchemas}
              selectedViewIds={editorRequiredViews}
              code={editorCode}
              onChange={setEditorCode}
              onPreview={selectedViz ? handlePreview : null}
            />
          ) : (
            <>
              <DataSchemaPanel
                selectedViewIds={editorRequiredViews}
                viewSchemas={viewSchemas}
                onInsertSchema={handleInsertSchema}
              />
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Visualization Code (Plotly.js)
                  </label>
                </div>
                <VizCodeEditor
                  value={editorCode}
                  onChange={setEditorCode}
                  onExecute={handlePreview}
                  onEditorMount={handleEditorMount}
                  height="400px"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Define a <code>render(data, params)</code> function that returns a Plotly spec.
                </p>
              </div>
            </>
          )}

          {/* Execution Error Panel */}
          <ErrorPanel
            error={executionError}
            onGoToLine={handleGoToLine}
          />

          {/* Console Panel */}
          <ConsolePanel
            logs={consoleLogs}
            onClear={() => setConsoleLogs([])}
          />

          {/* Save/validation error */}
          {editorError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {editorError}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            {selectedViz && (
              <button
                onClick={handlePreview}
                disabled={isRendering}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {isRendering ? 'Rendering...' : 'Preview'}
              </button>
            )}
            <button
              onClick={() => setShowEditor(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>

          {/* Preview */}
          {selectedViz && (
            <div className="mt-4 border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Preview</h4>
              <div
                ref={previewRef}
                className="h-80 border rounded-lg bg-gray-50"
              />
            </div>
          )}
        </div>
      ) : !showViewer ? (
        /* Visualizations List — folder-grouped (Grafana-style) */
        <div className="space-y-2">
          {isLoading ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              Loading visualizations...
            </div>
          ) : visualizations.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              <p className="mb-4">No visualizations yet.</p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={handleNewViz}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Create Visualization
                </button>
                <button
                  onClick={handleOpenGallery}
                  className="px-4 py-2 border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-50"
                >
                  ✦ Browse Gallery
                </button>
              </div>
            </div>
          ) : (
            [...folders.entries()].map(([folderName, vizList]) => {
              const isCollapsed = collapsedFolders.has(folderName);
              const folderMissingCount = vizList.filter((v) => vizStatuses[v.id] && !vizStatuses[v.id].ok).length;
              const folderOkCount = vizList.filter((v) => vizStatuses[v.id]?.ok).length;

              return (
                <div key={folderName} className="bg-white rounded-lg shadow overflow-hidden">
                  {/* Folder header */}
                  <button
                    onClick={() => toggleFolder(folderName)}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition text-left border-b border-gray-200"
                  >
                    <span className="text-gray-400 text-xs w-3">{isCollapsed ? '▶' : '▼'}</span>
                    <span className="font-medium text-gray-800">{folderName}</span>
                    <span className="text-xs text-gray-400">({vizList.length})</span>
                    {folderMissingCount > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full border border-amber-200">
                        ⚠ {folderMissingCount} needs sync
                      </span>
                    )}
                    {folderOkCount > 0 && folderMissingCount === 0 && (
                      <span className="ml-1 px-1.5 py-0.5 bg-green-50 text-green-700 text-xs rounded-full border border-green-200">
                        ✓ {folderOkCount} ready
                      </span>
                    )}
                  </button>

                  {/* Folder contents */}
                  {!isCollapsed && (
                    <table className="min-w-full">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Views</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {vizList.map((viz) => {
                          const status = vizStatuses[viz.id];
                          const isFailing = status && !status.ok;
                          return (
                            <tr key={viz.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900">{viz.name}</span>
                                  {viz.source === 'reference' && (
                                    <span className="px-1.5 py-0.5 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded">Gallery</span>
                                  )}
                                  {isFailing && (
                                    <span
                                      title={status.missingTable ? `Needs ${status.missingTable} synced` : 'Cannot render'}
                                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200"
                                    >
                                      ⚠ {status.missingTable
                                        ? `needs ${status.missingTable.includes('__') ? status.missingTable.split('__').slice(1).join('__') : status.missingTable}`
                                        : 'unavailable'}
                                    </span>
                                  )}
                                  {status?.ok && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">✓</span>
                                  )}
                                </div>
                                {viz.description && (
                                  <div className="text-sm text-gray-500 mt-0.5">{viz.description}</div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {viz.required_views?.length || 0}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <div className="flex gap-2">
                                  <button onClick={() => handleViewViz(viz.id)} className="text-green-600 hover:text-green-800">View</button>
                                  <button onClick={() => handleEditViz(viz.id)} className="text-primary-600 hover:text-primary-800">Edit</button>
                                  <button onClick={() => handleClone(viz.id)} className="text-purple-600 hover:text-purple-800">Clone</button>
                                  <button onClick={() => handleExport(viz.id)} className="text-gray-600 hover:text-gray-800">Export</button>
                                  <button onClick={() => handleDelete(viz.id)} className="text-red-600 hover:text-red-800">Delete</button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })
          )}
        </div>

      ) : null}

      {/* Gallery slide-in panel — rendered outside the editor/viewer ternary */}
      {showGallery && (
          <div className="fixed inset-0 z-50 flex">
            {/* Backdrop */}
            <div className="flex-1 bg-black/30" onClick={() => setShowGallery(false)} />
            {/* Panel */}
            <div className="w-[480px] bg-white shadow-2xl flex flex-col">
              <div className="flex items-center justify-between px-5 py-4 border-b">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Gallery</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Pre-built visualizations — add to your library</p>
                </div>
                <button onClick={() => setShowGallery(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                {isLoadingGallery ? (
                  <div className="text-center text-gray-500 py-12">Loading gallery...</div>
                ) : galleryError ? (
                  <div className="text-center text-red-500 py-12">{galleryError}</div>
                ) : galleryItems.length === 0 ? (
                  <div className="text-center text-gray-400 py-12">No gallery items available</div>
                ) : (
                  <div className="space-y-3">
                    {galleryItems.map((item) => {
                      const refId = item.ref_id || item.id;
                      const isInstalled = installedRefIds.has(refId);
                      const isInstalling = installingRefId === refId;
                      return (
                        <div key={refId} className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-gray-900 truncate">{item.name}</span>
                                {item.category && (
                                  <span className="shrink-0 px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">{item.category}</span>
                                )}
                              </div>
                              {item.description && (
                                <p className="text-sm text-gray-500 line-clamp-2">{item.description}</p>
                              )}
                              {item.tags?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {item.tags.map((tag) => (
                                    <span key={tag} className="px-1.5 py-0.5 text-xs bg-blue-50 text-blue-600 rounded">{tag}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="shrink-0">
                              {isInstalled ? (
                                <span className="px-3 py-1.5 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded-lg">
                                  ✓ Installed
                                </span>
                              ) : (
                                <button
                                  onClick={() => handleInstallFromGallery(refId)}
                                  disabled={isInstalling}
                                  className="px-3 py-1.5 text-xs font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition"
                                >
                                  {isInstalling ? 'Installing…' : 'Add'}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
    </div>
  );
}
